import { useEffect, useMemo, useState } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router';
import { App, Form } from 'antd';
import { useTranslation } from 'translations/hooks';
import { Routes } from 'business/router/constants';
import { useSession } from 'technical/auth/client';
import { getLocalStorageValue } from 'technical/local-storage';
import {
  SCAN_SITE_KEY,
  SEGMENT_TYPES_KEY,
} from 'technical/local-storage/constants';
import {
  scanSiteSchema,
  segmentTypesSchema,
} from 'technical/local-storage/types';
import type { SegmentFormValues } from 'business/receipt/pages/choose-segment-status/types';
import { useGetQuarantineDefectTypeData } from './data/use-get-quarantine-defect-type';
import { useUpdateSegmentStatusMutation } from './use-update-segment-status-mutation';
import { useCreateSegmentMutation } from './use-create-segment-mutation';
import type {
  SegmentCreateV1InputDto,
  SegmentUpdateV1InputDto,
} from 'generated/apiSchemas';
import { SegmentStatus } from 'business/receipt/constants';

export const useUpdateSegmentStatus = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { message } = App.useApp();

  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);

  const id = searchParams.get('id');
  const serialNumber = searchParams.get('serialNumber');
  const segmentType = searchParams.get('segmentType');
  const manufactureDate = searchParams.get('manufactureDate');
  const mold = searchParams.get('mold');
  const defaultStatus = searchParams.get('defaultStatus');
  const currentStatus = searchParams.get('status');
  const lastScanSite = searchParams.get('scanSite');

  const { createSegment } = useCreateSegmentMutation();
  const { updateSegmentStatus } = useUpdateSegmentStatusMutation();

  const segmentTypes = getLocalStorageValue({
    key: SEGMENT_TYPES_KEY,
    schema: segmentTypesSchema,
  });
  const scanSite = getLocalStorageValue({
    key: SCAN_SITE_KEY,
    schema: scanSiteSchema,
  });

  const { data: quarantineDefectTypesData } = useGetQuarantineDefectTypeData();
  const quarantineDefectTypes = quarantineDefectTypesData?.defectTypes || [];

  useEffect(() => {
    if (!segmentTypes?.length || !serialNumber) {
      const errorKey = !serialNumber
        ? 'chooseSegmentStatus.segmentNotFound'
        : 'chooseSegmentStatus.noSegmentTypes';
      message.error(t(errorKey));
      navigate(Routes.App);
    }
  }, [segmentTypes, serialNumber, navigate, message, t]);

  const handleSaveStatus = async (row: SegmentFormValues) => {
    setIsLoading(true);
    const formData = new FormData();

    formData.append('status', row.status);
    formData.append('segmentType', row.segmentType);
    formData.append('scanSiteId', scanSite?.id ?? 'no-scan-site');

    if (row.status === SegmentStatus.Quarantine) {
      formData.append('quarantineDefectType', row.quarantineType ?? '');
      formData.append('quarantineLocalization', row.localization);
      formData.append('quarantineDescription', row.description ?? '');
      formData.append('quarantinePositionX', String(row.position.x));
      formData.append('quarantinePositionY', String(row.position.y));

      if (row.pictures && row.pictures.length > 0) {
        row.pictures.forEach((picture) => {
          const file = picture.originFileObj;
          if (file instanceof Blob) {
            formData.append('quarantinePictures', file);
          }
        });
      }
    }

    if (row.status === SegmentStatus.Rejected) {
      formData.append('rejectionDescription', row.rejectionDescription ?? '');

      if (row.rejectionPictures && row.rejectionPictures.length > 0) {
        row.rejectionPictures.forEach((picture) => {
          const file = picture.originFileObj;
          if (file instanceof Blob) {
            formData.append('rejectionPictures', file);
          }
        });
      }
    }

    const config = {
      onSuccess: () => {
        setIsLoading(false);
        navigate(
          `${Routes.ReceiptSuccess}?${createSearchParams({
            serialNumber: serialNumber ?? '',
            segmentType: row.segmentType,
            status: row.status,
          })}`,
        );
      },
      onError: () => {
        setIsLoading(false);
        message.error(t('common.technicalError'));
      },
    };

    if (!id) {
      formData.append('serialNumber', serialNumber ?? '');
      formData.append('projectId', session?.user.projectId ?? '');
      createSegment(
        {
          body: formData as unknown as SegmentCreateV1InputDto,
          headers: { 'Content-Type': 'multipart/form-data' },
        },
        config,
      );
    } else {
      updateSegmentStatus(
        {
          pathParams: { id },
          body: formData as unknown as SegmentUpdateV1InputDto,
          headers: { 'Content-Type': 'multipart/form-data' },
        },
        config,
      );
    }
  };

  const initialValues = useMemo(() => {
    return {
      ...(segmentType && { segmentType }),
      ...(defaultStatus && { status: defaultStatus }),
    };
  }, [segmentType, defaultStatus]);

  return {
    form,
    serialNumber,
    segmentType,
    manufactureDate,
    mold,
    currentStatus,
    lastScanSite,
    segmentTypes,
    quarantineDefectTypes,
    initialValues,
    handleSaveStatus,
    isLoading,
  };
};
